import {
  Badge,
  ColorPicker,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Tooltip,
  Tour,
} from "antd";
import { API } from "../../../helpers/API/amplifyApi";
import { useEffect, useMemo, useRef, useState } from "react";
import Avatar from "react-avatar";
import { useSelector } from "react-redux";
import { driveApi } from "../../DriveRequest";
import GoogleDriveUpload from "./GoogleDriveUpload";
import { CloseIcon } from "../icons";
import "./Profile.scss";
import { useAuth } from "../../../authentication/authHelpers";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { toSentenceCase } from "../../../utils";
import rgbToHex from "../../../utils/rbgToHex";
import { editEmployee } from "../../../store/actions";
import { useDispatch } from "react-redux";
import useOpenOnce from "../../../hooks/useOpenOnce";
import { eventTracker } from "../../../utils/eventTracker";
import ChangePasswordForm from "./ChangePasswordForm";

const Profile = () => {
  const { Option } = Select;
  const [form] = Form.useForm();
  const { updateAuthCtx, user: authUser, userAccess } = useAuth();
  const dispatch = useDispatch();
  const mfaFeature = useRef();
  const [openFeatureAlert, setOpenFeatureAlert] = useOpenOnce(
    "2FaUserProfile",
    "newFeature"
  );

  const { employees } = useSelector((state) => state.employeesList);
  const { accessToken } = useSelector((state) => state.accessToken);
  const { users, isDarkMode } = useSelector((state) => state.usersList);
  const driveRequest = driveApi({ accessToken });

  const [userDBInfo, setUserDBInfo] = useState({});
  const [userPhoto, setUserPhoto] = useState("");
  const [spinning, setSpinning] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentUserRecord = useMemo(() => {
    const me = authUser || userAccess?.[0];
    if (Array.isArray(users)) {
      const identityId = me?.identityId;
      const email = (me?.email || "").toLowerCase();
      return (
        users.find((u) => identityId && u?.identityId === identityId) ||
        users.find((u) => email && (u?.email || "").toLowerCase() === email) ||
        me ||
        null
      );
    }
    return users || me || null;
  }, [users, authUser, userAccess]);

  let ls = JSON.parse(sessionStorage.getItem("agThemePreference"));

  const employeeObj = useMemo(() => {
    if (!currentUserRecord || !employees) return {};
    return (
      (employees || []).find(
        (el) => el?.userSub === currentUserRecord?.userSub,
      ) || {}
    );
  }, [currentUserRecord, employees]);

  //region PICKER SUCCESS
  const onPickerSuccess = async (params) => {
    const photoId = params?.uploadedFiles?.[0]?.id || "";
    if (!!photoId) {
      let promises = [];
      if (!!employeeObj?.googleDriveFileId) {
        promises.push(
          driveRequest.deleteDriveItem(employeeObj?.googleDriveFileId)
        );
      }
      promises.push(
        API.put("employees", `/employees/${employeeObj?.employeeId}`, {
          body: { googleDriveFileId: photoId },
        }).then((res) => dispatch(editEmployee(res?.employee?.Item)))
      );
      await Promise.allSettled(promises).then(() =>
        message.success({
          key: "profile",
          content: "Imazhi u ngarkua me sukses!",
        })
      );
    }
  };

  //region DELETE
  const handlePhotoDelete = async () => {
    let promises = [];
    promises.push(driveRequest.deleteDriveItem(employeeObj?.googleDriveFileId));
    promises.push(
      API.put("employees", `/employees/${employeeObj?.employeeId}`, {
        body: { googleDriveFileId: "" },
      })
    );
    eventTracker("Profile", "click", "Deleted profile photo");

    await Promise.allSettled(promises).then(() => {
      setUserPhoto("");
      message.success({
        key: "profile",
        content: "Imazhi u fshi me sukses!",
      });
    });
  };

  //region >validatePin()
  const validateNewPin = (newVal) => {
    let notSafe = [
      "123456",
      "password",
      "12345678",
      "qwerty",
      "123456789",
      "1234567890",
      "12345",
      "1234",
      "1111",
      "0000",
    ];
    if (notSafe.includes(newVal)) {
      message.warning({ key: "notSafe", content: "This pin is not safe!" });
    }
  };
  //region ON SAVE
  const onSave = async () => {
    setSaving(true);
    form
      .validateFields()
      .then(async (res) => {
        eventTracker("Profile", "click", "Updated preferences");

        const { metaColor } = res?.accentColor || {};
        let accentColor = res?.accentColor;

        if (!!metaColor) {
          const { r, g, b } = metaColor || {};
          accentColor = rgbToHex(r, g, b);
        }

        const { theme, mode } = res;
        sessionStorage.setItem(
          "agThemePreference",
          JSON.stringify({ theme, mode, accentColor })
        );

        await API.put("users", `/users/${userDBInfo?.identityId}`, {
          body: {
            pinNumber: res?.pinNumber,
            idleTimeout: res?.idleTimeout,
            auth2Fa: {
              ...userDBInfo?.auth2Fa,
              is2FaActive: res?.is2FaActive,
            },
          },
        })
          .then(() => {
            message.success({
              key: "success",
              content: "U ndryshua me sukses!",
            });
            updateAuthCtx((prev) => ({
              ...prev,
              auth2Fa: {
                ...(prev.auth2Fa || {}),
                is2FaActive: res?.is2FaActive,
              },
            }));
            setSaving(false);
          })
          .catch(() => {
            setSaving(false);
            message.error({ key: "error", content: "Ndodhi nje gabim!" });
          });
      })
      .catch((err) => {
        setSaving(false);
        console.log("Error validating fields: ", err);
      });
  };

  //region >getImgDrive()
  const getImageFromDrive = (fileId) => {
    if (!!fileId) {
      driveRequest
        .getImageSrc(fileId)
        .then((image) => {
          setUserPhoto(image?.src);
        })
        .catch((err) => console.log(err))
        .finally(() => setSpinning(false));
    } else {
      setInterval(() => {
        setSpinning(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (!currentUserRecord) return;

    const defaultTheme = isDarkMode
      ? {
          theme: "alpine",
          mode: "dark",
          accentColor: "#213c4f",
        }
      : {
          theme: "alpine",
          mode: "light",
          accentColor: "#1d3445",
        };

    Object.entries(ls || defaultTheme).forEach(([key, value]) => {
      form.setFieldValue(key, value);
    });

    form.setFieldValue("pinNumber", currentUserRecord?.pinNumber);
    form.setFieldValue("idleTimeout", currentUserRecord?.idleTimeout);
    form.setFieldValue("is2FaActive", currentUserRecord?.auth2Fa?.is2FaActive);

    setUserDBInfo(currentUserRecord);
    getImageFromDrive(employeeObj?.googleDriveFileId);
  }, [currentUserRecord, employeeObj, isDarkMode]);
  const rules = [
    {
      required: true,
      message: "Kjo fushë nevojitet!",
    },
  ];
  const steps = [
    {
      title: "🚀 New Feature!",
      description: "Aktivizo verifikimin shtesë për logimin tuaj të ardhshëm.",
      target: () => mfaFeature.current,
    },
  ];

  const roleIntegrityTampered =
    currentUserRecord?.integrityStatus === "tampered" ||
    (currentUserRecord?.integrityOk === false &&
      currentUserRecord?.integrityStatus !== "legacy");

  //region RETURN
  return (
    <>
      <div style={{ width: "100%", height: "calc(100% - 35px)" }}>
        {roleIntegrityTampered ? (
          <div
            className="integrity-tamper-banner"
            data-testid="user-integrity-tamper-banner"
            role="alert"
            style={{
              margin: "8px 12px",
              padding: "10px 12px",
              borderRadius: 6,
              background: "#fee2e2",
              border: "1px solid #dc2626",
              color: "#991b1b",
              fontWeight: 600,
            }}
          >
            Role integrity cipher mismatch — userRole / accessConfig failed
            verification
            {currentUserRecord?.integrityCode
              ? ` (${currentUserRecord.integrityCode})`
              : ""}
          </div>
        ) : null}
        <div className="userPersonalInfo">
          <Spin spinning={spinning}>
            {!!userPhoto && (
              <div className="profile-img-div">
                <img className="profile-img" src={userPhoto || ""} />
                <Popconfirm
                  classNames={{
                    root: `globalPopover ${isDarkMode ? "dark" : ""}`,
                  }}
                  title="A jeni te sigurt?"
                  okText="Po."
                  cancelText="Jo, anullo!"
                  onConfirm={handlePhotoDelete}
                >
                  <div className="remove-profile-photo">
                    <CloseIcon />
                  </div>
                </Popconfirm>
              </div>
            )}
            {!spinning && !userPhoto && (
              <div className="userLettersName">
                <Avatar
                  name={users?.given_name + " " + users?.family_name}
                  size="150"
                  round={true}
                />
              </div>
            )}
          </Spin>
          <div className="userActions">
            <h2>{users?.given_name + " " + users?.family_name}</h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
              }}
            ></div>
            <GoogleDriveUpload
              employeeObj={employeeObj}
              accessToken={accessToken}
              setCurrentFile={setUserPhoto}
              // currentEmpl={currentEmpl}
              onPickerSuccess={onPickerSuccess}
              adminUpload={false}
              uploadImages={true}
            />
          </div>
          {/* !!! time to make this shit work  */}
          <Form form={form}>
            <div className="profileFormItem">
              <span>Pin Number:</span>
              <Form.Item rules={rules} name={"pinNumber"}>
                <Input.Password
                  type="password"
                  className="pasw-prof-input"
                  onChange={(e) => validateNewPin(e.target.value)}
                />
              </Form.Item>
            </div>
            <div className="profileFormItem">
              <span>Idle Time:</span>
              <Form.Item rules={rules} name={"idleTimeout"}>
                <Select>
                  {idleOptions?.map(({ value, label }) => (
                    <Option value={value}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="profileFormItem">
              <span>AG Grid theme:</span>
              <div className="agThemeDropdowns">
                <Tooltip placement="top" title="Main theme">
                  <Form.Item rules={rules} name={"theme"}>
                    <Select>
                      {themeOptions?.map((theme) => (
                        <Option value={theme}>{toSentenceCase(theme)}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Tooltip>
                <Tooltip placement="top" title="Theme mode">
                  <Form.Item rules={rules} name={"mode"}>
                    <Select>
                      {modeOptions?.map((mode) => (
                        <Option value={mode}>{toSentenceCase(mode)}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Tooltip>
                <Tooltip placement="top" title="Accent color">
                  <Form.Item rules={rules} name={"accentColor"}>
                    <ColorPicker />
                  </Form.Item>
                </Tooltip>
                <p>Tip: Light accents suit dark themes better :&#41;</p>
              </div>
            </div>
            <div className="profileFormItem" ref={mfaFeature}>
              <span>Enable 2FA</span>
              <Form.Item name={"is2FaActive"}>
                <Switch />
              </Form.Item>
            </div>
            <MondayButton
              className="mondayButtonGreen saveBtn"
              onClick={onSave}
              spin={saving}
            >
              Ruaj Ndryshimet
            </MondayButton>
          </Form>

          <section className="profileChangePassword">
            <h3>Ndrysho fjalëkalimin</h3>
            <ChangePasswordForm mode="voluntary" />
          </section>
        </div>
      </div>
      {openFeatureAlert ? (
        <Tour
          open={openFeatureAlert}
          mask={{ color: "#2a2b3a71" }}
          steps={steps}
          classNames={{ root: "newFeatureTour" }}
          onClose={() => setOpenFeatureAlert(false)}
        />
      ) : null}
    </>
  );
};

export default Profile;

const idleOptions = [
  { value: 300000, label: "5 minutes" },
  { value: 600000, label: "10 minutes" },
  { value: 3600000, label: "1 hour" },
  { value: 7200000, label: "2 hours" },
  { value: 14400000, label: "4 hours" },
];
const themeOptions = ["quartz", "balham", "material", "alpine"];
const modeOptions = ["light", "dark"];
